import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import {
    XtramBadge,
    XtramButton,
    XtramCheckbox,
    XtramNotice,
    XtramStat,
    XtramTable,
    xtramStyles as styles,
} from '../Widgets/XtramModal/XtramModal';

export const ComparisonKind = {
    Ledger: 'ledger',
    ProfitAndLoss: 'profitAndLoss',
};

/** What the lines of a profit and loss comparison are. */
export const ProfitAndLossBasis = {
    SortCodes: 'sortCodes',
    PalCodes: 'palCodes',
};

export const formatAmount = (value) => (value == null || value === ''
    ? '—'
    : Number(value).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

/** Amount cells: one line, digits aligned. */
const NUM = { sx: { whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' } };

/** A ledger whose only differences are the opening balances yet to be imported is a warning, not an error. */
export const differenceTone = (part) => {
    if (part.differentCount > 0) return 'error';
    return part.openingPendingCount > 0 ? 'warning' : 'success';
};

export const differenceLabel = (part, t) => {
    if (part.isSame) return t('rivhitBackup.comparison.same');
    return part.differentCount > 0
        ? t('rivhitBackup.comparison.differentCount', { count: part.differentCount })
        : t('rivhitBackup.comparison.openingPendingCount', { count: part.openingPendingCount });
};

const lineClass = (line) => {
    if (line.isSame) return undefined;
    return line.openingPending ? styles.rowWarning : styles.rowError;
};

/** The comparisons of a backup the response has, the primary one first. */
export const comparisonKinds = (comparison) => [comparison?.primary, ...Object.values(ComparisonKind)]
    .filter((kind, index, all) => kind && all.indexOf(kind) === index && comparison[kind]);

const byPalCode = (part) => part?.basis === ProfitAndLossBasis.PalCodes;

/** The entries of a simulated closing balance import, shown beside the comparisons. */
const ClosingBalanceView = 'closingBalance';

/** How the closing balance import finds the card of a balance, by its status. */
const CARD_RESOLUTIONS = [
    { key: 'matched', tone: 'success' },
    { key: 'nameMatch', tone: 'info' },
    { key: 'newCard', tone: 'warning' },
    { key: 'newCardWithExternalId', tone: 'warning' },
];

const cardResolutionOf = (status) => (typeof status === 'number'
    ? CARD_RESOLUTIONS[status]
    : CARD_RESOLUTIONS.find((r) => r.key.toLowerCase() === `${status}`.toLowerCase())) ?? { key: null, tone: undefined };

/**
 * The backup's books of a year against the client's: the cards' balances (as in the ledger report) and the
 * profit and loss by sort code – by main account when the year was imported as closing balances. The primary one
 * follows the client's bookkeeping – the balances for double-entry books, the profit and loss for single-entry ones.
 */
const RivhitBackupComparison = ({ comparison, direction }) => {
    const { t } = useTranslation();
    const tc = (key, options) => t(`rivhitBackup.comparison.${key}`, options);
    const kinds = useMemo(() => comparisonKinds(comparison), [comparison]);
    const views = useMemo(
        () => [...kinds, ...(comparison?.closingBalance ? [ClosingBalanceView] : [])],
        [kinds, comparison]
    );
    const [kind, setKind] = useState(views[0] ?? null);
    const [onlyDifferences, setOnlyDifferences] = useState(true);

    useEffect(() => {
        setKind((current) => (views.includes(current) ? current : views[0] ?? null));
    }, [views]);

    if (!comparison) return null;
    if (views.length === 0) return <XtramNotice type="info">{tc('noComparison')}</XtramNotice>;

    const isClosingBalance = kind === ClosingBalanceView;
    const part = isClosingBalance ? null : comparison[kind];
    const lines = (part?.lines || []).filter((line) => !onlyDifferences || !line.isSame);
    const isLedger = kind === ComparisonKind.Ledger;
    const isByPalCode = !isLedger && byPalCode(part);
    const simulated = !!comparison.simulated;
    const systemLabel = (key) => tc(simulated ? `${key}AfterImport` : key);
    const primaryHelp = () => {
        if (!comparison.isDoubleEntry) return tc('primarySingleEntry');
        return comparison.primary === ComparisonKind.Ledger ? tc('primaryDoubleEntry') : tc('primaryNoLedger');
    };
    const kindLabel = (k) => (k === ComparisonKind.ProfitAndLoss && byPalCode(comparison[k]) ? tc('profitAndLossByPalCode') : tc(k));
    const backupProfit = part?.backupProfit ?? 0;
    const systemProfit = part?.systemProfit ?? 0;

    const renderLedger = () => (
        <Table size="small" stickyHeader dir={direction}>
            <TableHead>
                <TableRow>
                    <TableCell>{tc('card')}</TableCell>
                    <TableCell>{tc('name')}</TableCell>
                    <TableCell>{tc('palName')}</TableCell>
                    <TableCell {...NUM}>{tc('opening')}</TableCell>
                    <TableCell {...NUM}>{tc('debit')}</TableCell>
                    <TableCell {...NUM}>{tc('credit')}</TableCell>
                    <TableCell {...NUM}>{tc('backupBalance')}</TableCell>
                    <TableCell {...NUM}>{systemLabel('systemBalance')}</TableCell>
                    <TableCell {...NUM}>{tc('difference')}</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {lines.length === 0 && (
                    <TableRow>
                        <TableCell colSpan={9} className={styles.emptyState}>{tc('noDifferences')}</TableCell>
                    </TableRow>
                )}
                {lines.map((line) => (
                    <TableRow key={`${line.cardId}-${line.systemCardId}`} className={lineClass(line)}>
                        <TableCell>
                            {line.cardId || line.systemCardId}
                            {line.systemCardId && line.cardId && line.systemCardId !== line.cardId && (
                                <span className={styles.fieldHint}> ({line.systemCardId})</span>
                            )}
                            {!line.systemCardId && <> <XtramBadge tone="warning">{tc('missingInSystem')}</XtramBadge></>}
                        </TableCell>
                        <TableCell>{line.name || '—'}</TableCell>
                        <TableCell>
                            {line.palName || line.palCode || '—'}
                            {line.isResult && <> <XtramBadge tone="info">{tc('result')}</XtramBadge></>}
                        </TableCell>
                        <TableCell {...NUM}>{formatAmount(line.backupOpening)}</TableCell>
                        <TableCell {...NUM}>{formatAmount(line.backupDebit)}</TableCell>
                        <TableCell {...NUM}>{formatAmount(line.backupCredit)}</TableCell>
                        <TableCell {...NUM}>{formatAmount(line.backupBalance)}</TableCell>
                        <TableCell {...NUM}>{formatAmount(line.systemBalance)}</TableCell>
                        <TableCell {...NUM} className={line.isSame ? undefined : line.openingPending ? styles.textWarning : styles.textError}>
                            {formatAmount(line.difference)}
                            {line.openingPending && <> <XtramBadge tone="warning">{tc('openingPending')}</XtramBadge></>}
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );

    const renderProfitAndLoss = () => (
        <Table size="small" stickyHeader dir={direction}>
            <TableHead>
                <TableRow>
                    <TableCell>{tc('sortCode')}</TableCell>
                    <TableCell>{tc('name')}</TableCell>
                    <TableCell>{tc('group')}</TableCell>
                    <TableCell>{tc('type')}</TableCell>
                    <TableCell>{tc('rows')}</TableCell>
                    <TableCell {...NUM}>{tc('backupAmount')}</TableCell>
                    <TableCell {...NUM}>{systemLabel('systemAmount')}</TableCell>
                    <TableCell {...NUM}>{tc('difference')}</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {lines.length === 0 && (
                    <TableRow>
                        <TableCell colSpan={8} className={styles.emptyState}>{tc('noDifferences')}</TableCell>
                    </TableRow>
                )}
                {lines.map((line) => (
                    <TableRow
                        key={`${line.sortCodeId}-${line.backupSortCodeId}`}
                        className={line.isSame ? undefined : styles.rowError}
                    >
                        <TableCell>
                            {line.backupSortCodeId || line.sortCodeId}
                            {line.sortCodeId && line.backupSortCodeId && line.sortCodeId !== line.backupSortCodeId && (
                                <span className={styles.fieldHint}> ({line.sortCodeId})</span>
                            )}
                            {line.backupSortCodeId && !line.sortCodeId && (
                                <> <XtramBadge tone="warning">{tc('missingInSystem')}</XtramBadge></>
                            )}
                        </TableCell>
                        <TableCell>{line.name || '—'}</TableCell>
                        <TableCell>
                            {line.systemGroup || '—'}
                            {line.backupRowCount > 0 && !line.inBackupProfitAndLoss && (
                                <> <XtramBadge>{tc('notInBackupProfitAndLoss')}</XtramBadge></>
                            )}
                            {line.sortCodeId && !line.inSystemProfitAndLoss && (
                                <> <XtramBadge tone="warning">{tc('notInSystemProfitAndLoss')}</XtramBadge></>
                            )}
                        </TableCell>
                        <TableCell>{line.isIncome ? tc('income') : tc('expense')}</TableCell>
                        <TableCell>{line.backupRowCount}</TableCell>
                        <TableCell {...NUM}>{formatAmount(line.backupAmount)}</TableCell>
                        <TableCell {...NUM}>{formatAmount(line.systemAmount)}</TableCell>
                        <TableCell {...NUM} className={line.isSame ? undefined : styles.textError}>
                            {formatAmount(line.difference)}
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );

    const renderPalCodes = () => (
        <Table size="small" stickyHeader dir={direction}>
            <TableHead>
                <TableRow>
                    <TableCell>{tc('palName')}</TableCell>
                    <TableCell>{tc('type')}</TableCell>
                    <TableCell>{tc('cards')}</TableCell>
                    <TableCell {...NUM}>{tc('backupAmount')}</TableCell>
                    <TableCell {...NUM}>{systemLabel('systemAmount')}</TableCell>
                    <TableCell {...NUM}>{tc('difference')}</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {lines.length === 0 && (
                    <TableRow>
                        <TableCell colSpan={6} className={styles.emptyState}>{tc('noDifferences')}</TableCell>
                    </TableRow>
                )}
                {lines.map((line) => (
                    <TableRow key={line.sortCodeId || '—'} className={line.isSame ? undefined : styles.rowError}>
                        <TableCell>
                            {line.name || '—'}
                            {line.backupRowCount === 0 && <> <XtramBadge tone="warning">{tc('onlyInSystem')}</XtramBadge></>}
                        </TableCell>
                        <TableCell>{line.isIncome ? tc('income') : tc('expense')}</TableCell>
                        <TableCell>{line.backupRowCount}</TableCell>
                        <TableCell {...NUM}>{formatAmount(line.backupAmount)}</TableCell>
                        <TableCell {...NUM}>{formatAmount(line.systemAmount)}</TableCell>
                        <TableCell {...NUM} className={line.isSame ? undefined : styles.textError}>
                            {formatAmount(line.difference)}
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );

    const closing = comparison.closingBalance;
    const closingEntries = closing?.entries || [];

    const renderClosingBalance = () => (
        <Table size="small" stickyHeader dir={direction}>
            <TableHead>
                <TableRow>
                    <TableCell>{tc('card')}</TableCell>
                    <TableCell>{tc('name')}</TableCell>
                    <TableCell>{tc('palName')}</TableCell>
                    <TableCell {...NUM}>{tc('amount')}</TableCell>
                    <TableCell>{tc('side')}</TableCell>
                    <TableCell>{tc('cardStatus')}</TableCell>
                    <TableCell>{tc('systemCard')}</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {closingEntries.length === 0 && (
                    <TableRow>
                        <TableCell colSpan={7} className={styles.emptyState}>{tc('noClosingBalances')}</TableCell>
                    </TableRow>
                )}
                {closingEntries.map((entry) => {
                    const resolution = cardResolutionOf(entry.cardResolutionStatus);
                    const isCredit = entry.amount < 0;
                    return (
                        <TableRow key={entry.custId}>
                            <TableCell>{entry.custId}</TableCell>
                            <TableCell>{entry.custName || '—'}</TableCell>
                            <TableCell>{entry.palCodeName || entry.palCode || '—'}</TableCell>
                            <TableCell {...NUM} className={isCredit ? styles.textError : undefined}>
                                {formatAmount(Math.abs(entry.amount))}
                            </TableCell>
                            <TableCell>{isCredit ? tc('credit') : tc('debit')}</TableCell>
                            <TableCell>
                                {resolution.key
                                    ? <XtramBadge tone={resolution.tone}>{tc(`cardResolution.${resolution.key}`)}</XtramBadge>
                                    : '—'}
                            </TableCell>
                            <TableCell>{entry.resolvedCardId || '—'}</TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );

    const renderTable = () => {
        if (isClosingBalance) return renderClosingBalance();
        if (isLedger) return renderLedger();
        return isByPalCode ? renderPalCodes() : renderProfitAndLoss();
    };

    const renderClosingStats = () => (
        <div className={styles.stats}>
            <XtramStat label={tc('totalDebit')} value={formatAmount(closing.totalDebit)} />
            <XtramStat label={tc('totalCredit')} value={formatAmount(closing.totalCredit)} />
            <XtramStat
                label={tc('balanceDifference')}
                value={formatAmount(closing.balance)}
                tone={Math.abs(closing.balance ?? 0) > (comparison.tolerance ?? 0) ? 'error' : 'success'}
            />
            <XtramStat label={tc('cardsWithBalance')} value={closingEntries.length} />
            <XtramStat label={tc('newCards')} value={closing.newCardsToCreate} tone={closing.newCardsToCreate > 0 ? 'warning' : undefined} />
            <XtramStat
                label={tc('externalIdCards')}
                value={closing.cardsWithExternalIdMapping}
                tone={closing.cardsWithExternalIdMapping > 0 ? 'warning' : undefined}
            />
        </div>
    );

    /** Double-entry books balance when the year's debits come to its credits – the backup's and the system's. */
    const renderBalanceCheck = () => (
        <div className={styles.stats}>
            {[
                { key: 'backup', label: tc('backupBooks'), debit: part.backupDebit, credit: part.backupCredit, balanced: part.isBackupBalanced },
                { key: 'system', label: systemLabel('systemBooks'), debit: part.systemDebit, credit: part.systemCredit, balanced: part.isSystemBalanced },
            ].map((side) => (
                <React.Fragment key={side.key}>
                    <XtramStat label={`${side.label} · ${tc('totalDebit')}`} value={formatAmount(side.debit)} />
                    <XtramStat label={`${side.label} · ${tc('totalCredit')}`} value={formatAmount(side.credit)} />
                    <XtramStat
                        label={`${side.label} · ${tc('balanceDifference')}`}
                        value={side.balanced
                            ? tc('balanced')
                            : tc('notBalanced', { difference: formatAmount((side.debit ?? 0) - (side.credit ?? 0)) })}
                        tone={side.balanced ? 'success' : 'error'}
                    />
                </React.Fragment>
            ))}
        </div>
    );

    return (
        <div className={styles.section}>
            <XtramNotice type="info">
                {simulated && <strong>{tc('simulatedHelp', { count: comparison.importedEntryCount ?? 0 })}</strong>}
                <span>{primaryHelp()}</span>
                {byPalCode(comparison[ComparisonKind.ProfitAndLoss]) && <span>{tc('byPalCodeHelp')}</span>}
                {comparison[ComparisonKind.Ledger]?.openingPendingCount > 0 && (
                    <span>{tc('openingPendingHelp', { year: comparison.year - 1 })}</span>
                )}
                <span className={styles.fieldHint}>{tc('tolerance', { tolerance: formatAmount(comparison.tolerance) })}</span>
            </XtramNotice>
            <div className={styles.toolbar}>
                <div className={styles.toolbarGroup}>
                    {kinds.map((k) => (
                        <XtramButton key={k} variant="ghost" active={k === kind} onClick={() => setKind(k)}>
                            {kindLabel(k)}
                            {k === comparison.primary && ` · ${tc('primary')}`}
                            {' '}
                            <XtramBadge tone={differenceTone(comparison[k])}>{differenceLabel(comparison[k], t)}</XtramBadge>
                        </XtramButton>
                    ))}
                    {closing && (
                        <XtramButton variant="ghost" active={isClosingBalance} onClick={() => setKind(ClosingBalanceView)}>
                            {tc('closingBalance')}{' '}
                            <XtramBadge tone="info">{closingEntries.length}</XtramBadge>
                        </XtramButton>
                    )}
                </div>
                {!isClosingBalance && (
                    <XtramCheckbox checked={onlyDifferences} onChange={setOnlyDifferences} label={tc('onlyDifferences')} />
                )}
            </div>
            {isClosingBalance && (
                <XtramNotice type="info">{tc('closingBalanceHelp', { year: comparison.year })}</XtramNotice>
            )}
            {isClosingBalance ? renderClosingStats() : (
                <div className={styles.stats}>
                    <XtramStat label={tc('backupProfit')} value={formatAmount(backupProfit)} />
                    <XtramStat label={systemLabel('systemProfit')} value={formatAmount(systemProfit)} />
                    <XtramStat
                        label={tc('profitDifference')}
                        value={formatAmount(backupProfit - systemProfit)}
                        tone={Math.abs(backupProfit - systemProfit) > (comparison.tolerance ?? 0) ? 'error' : 'success'}
                    />
                    <XtramStat label={tc('sameLines')} value={part.sameCount} tone="success" />
                    <XtramStat label={tc('differentLines')} value={part.differentCount} tone={part.differentCount > 0 ? 'error' : undefined} />
                    {isLedger && part.openingPendingCount > 0 && (
                        <XtramStat label={tc('openingPendingLines')} value={part.openingPendingCount} tone="warning" />
                    )}
                    {isLedger && (
                        <XtramStat label={tc('missingCards')} value={part.missingCardCount} tone={part.missingCardCount > 0 ? 'warning' : undefined} />
                    )}
                    {!isLedger && !isByPalCode && (
                        <XtramStat
                            label={tc('missingSortCodes')}
                            value={part.missingSortCodeCount}
                            tone={part.missingSortCodeCount > 0 ? 'warning' : undefined}
                        />
                    )}
                </div>
            )}
            {isLedger && comparison.isDoubleEntry && part.backupDebit != null && renderBalanceCheck()}
            <XtramTable height="tall">{renderTable()}</XtramTable>
        </div>
    );
};

export default RivhitBackupComparison;
